import { FormLabel, FormErrorMessage, Flex, Input, FormControl, Checkbox, FormHelperText, Textarea, Box, Tooltip, CloseButton } from "@chakra-ui/react";
import { Button, FileContainer, FileUploader } from "am-tax-fe-core";
import { SubmitHandler, useForm } from "react-hook-form";
import { CreateOrUpdateJurisdiction, Jurisdiction as TransformedJurisdictionData } from "../../../api/jurisdictions/jurisdictionApi";
import { useCreateJurisdiction, useUpdateJurisdiction } from "../../../api/jurisdictions/jurisdictionQueries";
import { useState } from "react";
import { ALLOWED_JURISDICTION_OVERVIEW_EXTENSIONS } from "../../../utils/constants";

interface Props {
    onClose: () => void;
    jurisdictionData: TransformedJurisdictionData | undefined;
    existingJurisdictions: TransformedJurisdictionData[] | undefined;
    isEdit: boolean;
}

type FormFields = {
    id: string;
    name: string;
    abbreviation: string;
    introductionDescription: string;
    jurisdictionOverview: string;
    status: string;
    activate: boolean;
    regulatoryLanguage: string;
};

export const CreateOrEditJurisdictionForm = ({ jurisdictionData, isEdit, onClose }: Props) => {
    const [selectedJurisdictionOverviewFile, setSelectedJurisdictionOverviewFile] = useState<FileContainer | undefined>();
    const formValues = {
        id: jurisdictionData?.id || "",
        name: jurisdictionData?.name || "",
        abbreviation: jurisdictionData?.abbreviation || "",
        introductionDescription: jurisdictionData?.introductionDescription || "",
        jurisdictionOverview: jurisdictionData?.jurisdictionOverviewBlobPath || "",
        status: jurisdictionData?.active ? "Active" : "Inactive",
        activate: jurisdictionData?.active || false,
        regulatoryLanguage: jurisdictionData?.regulatoryLanguage || "",
    };

    const {
        register,
        setError,
        setValue,
        clearErrors,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<FormFields>({ values: formValues });

    const updateJurisdictionMutation = useUpdateJurisdiction();
    const createJurisdictionMutation = useCreateJurisdiction();

    const [hasJurisdictionDescription, setHasJurisdictionDescription] = useState<boolean>(
        !!jurisdictionData?.jurisdictionOverviewBlobPath && jurisdictionData?.jurisdictionOverviewBlobPath.split("/").length > 1,
    );

    const onSubmit: SubmitHandler<FormFields> = async data => {
        // Check if jurisdiction overview file is required and validate
        if (!selectedJurisdictionOverviewFile && !hasJurisdictionDescription && !data.jurisdictionOverview) {
            setError("jurisdictionOverview", {
                type: "manual",
                message: "Jurisdiction overview file is required",
            });
            return;
        }
        if (isEdit && jurisdictionData?.id) {
            const jurisdictionPayload: CreateOrUpdateJurisdiction = {
                Id: jurisdictionData.id,
                Name: data.name,
                Abbreviation: data.abbreviation,
                IntroductionDescription: data.introductionDescription,
                JurisdictionOverviewBlobPath: data.jurisdictionOverview || "/",
                Active: data.activate,
                RegulatoryLanguage: data.regulatoryLanguage,
            };
            await updateJurisdictionMutation.mutateAsync({ file: selectedJurisdictionOverviewFile, jurisdiction: jurisdictionPayload });
        } else {
            const jurisdictionPayload: CreateOrUpdateJurisdiction = {
                Name: data.name,
                Abbreviation: data.abbreviation,
                IntroductionDescription: data.introductionDescription,
                JurisdictionOverviewBlobPath: data.jurisdictionOverview || "/",
                Active: data.activate,
                RegulatoryLanguage: data.regulatoryLanguage,
            };
            await createJurisdictionMutation.mutateAsync({ file: selectedJurisdictionOverviewFile, jurisdiction: jurisdictionPayload });
        }
        onClose();
    };

    const onFileAdded = async (fileContainer: FileContainer) => {
        const fileName = fileContainer.file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

        if (ALLOWED_JURISDICTION_OVERVIEW_EXTENSIONS.includes(fileExtension)) {
            setSelectedJurisdictionOverviewFile(fileContainer);
            setValue("jurisdictionOverview", fileContainer.file.name);
            clearErrors("jurisdictionOverview");
        } else {
            setSelectedJurisdictionOverviewFile(undefined);
            setValue("jurisdictionOverview", "");
            setError("jurisdictionOverview", {
                type: "manual",
                message: "Invalid file format.",
            });
        }
    };

    const handleRemoveJurisdictionDescription = () => {
        setSelectedJurisdictionOverviewFile(undefined);
        setHasJurisdictionDescription(false);
        setValue("jurisdictionOverview", "");
        // This will trigger validation since the field is required
        setError("jurisdictionOverview", {
            type: "manual",
            message: "Jurisdiction overview file is required",
        });
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="2rem" flexFlow="column nowrap">
                    <Flex gap="1rem">
                        <FormControl isInvalid={!!errors?.name}>
                            <FormLabel>Name:</FormLabel>
                            <Input
                                {...register("name", {
                                    required: "Name is required.",
                                    validate: value => value.trim() !== "" || "Name is required.",
                                })}
                            />
                            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                        </FormControl>
                    </Flex>

                    <Flex gap="1rem">
                        <FormControl>
                            <FormLabel>Abbreviation:</FormLabel>
                            <Input {...register("abbreviation")} />
                        </FormControl>
                    </Flex>

                    <Flex gap="1rem">
                        <FormControl isInvalid={!!errors?.regulatoryLanguage}>
                            <FormLabel>Regulatory Language:</FormLabel>
                            <Input
                                {...register("regulatoryLanguage", {
                                    required: "Regulatory Language is required.",
                                    validate: value => value.trim() !== "" || "Regulatory Language is required.",
                                })}
                            />
                            <FormErrorMessage>{errors.regulatoryLanguage?.message}</FormErrorMessage>
                        </FormControl>
                    </Flex>

                    <Flex gap="1rem">
                        <FormControl isInvalid={!!errors?.introductionDescription}>
                            <FormLabel>Introduction Description:</FormLabel>
                            <Textarea {...register("introductionDescription")} />
                            <FormErrorMessage>{errors.introductionDescription?.message}</FormErrorMessage>
                        </FormControl>
                    </Flex>

                    <FormControl isInvalid={!!errors?.status}>
                        <FormLabel fontWeight="400">Status:</FormLabel>
                        <Checkbox {...register("activate")} checked={watch("activate")}>
                            {watch("activate") ? "Active" : "Inactive"}
                        </Checkbox>
                        <FormHelperText>Activate jurisdiction to allow use in report creation</FormHelperText>
                    </FormControl>

                    {hasJurisdictionDescription ? (
                        <Box>
                            <FormLabel>Jurisdiction Regulations:</FormLabel>
                            <Flex align="center" mt="1rem" mb="1rem">
                                <p>{jurisdictionData?.jurisdictionOverviewBlobPath?.split("/").pop()}</p>
                                <Tooltip label="Remove jurisdiction regulations">
                                    <CloseButton ml="1rem" variant="ghost" aria-label="Close" onClick={handleRemoveJurisdictionDescription} />
                                </Tooltip>
                            </Flex>
                            <FormErrorMessage>{errors.jurisdictionOverview?.message}</FormErrorMessage>
                        </Box>
                    ) : (
                        <FormControl isRequired isInvalid={!!errors?.jurisdictionOverview}>
                            <FormLabel>Jurisdiction Regulations:</FormLabel>
                            <FileUploader
                                {...register("jurisdictionOverview")}
                                maxSize={10485760}
                                maxFiles={1}
                                onFileAdded={onFileAdded}
                                accept={{
                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                }}
                            />
                            <FormErrorMessage>{errors.jurisdictionOverview?.message}</FormErrorMessage>
                        </FormControl>
                    )}

                    <Flex marginBottom=".75rem" justifyContent="flex-end">
                        <Button type="submit" variant="primary">
                            {isEdit ? "Update" : "Add"}
                        </Button>
                    </Flex>
                </Flex>
            </form>
        </>
    );
};
